<template>
	<Col>
		<div class="user-select-combobox">
			<AppCombobox
				dense
				hide-details
				ref="user_combo"
				item-text="itemId"
				:placeholder="placeholder"
				@blur="appendSearchToSelection"
				v-model="localComboboxSelection"
				:search-input.sync="searchString"
				:items="groupedTeamMembersToShareWith"
				@keydown.enter="appendSearchToSelection"
				:menu-props="{ closeOnClick: true, closeOnContentClick: true, maxHeight: 350 }"
			>
				<template v-slot:selection="{ item, parent }">
					<ChipOfContacts
						:item="item"
						class="selection-chip"
						@onClose="parent.selectItem(item)"
						:minimalBillingLevel="minimalBillingLevel"
					/>
				</template>
				<template v-slot:item="{ item }">
					<UserSelectComboboxItem :item="item" />
				</template>
				<template v-slot:no-data>
					<ShareDialogInteractiveEmailInvite v-if="searchString && canInviteToWorkspace" :email="searchString" />
				</template>
			</AppCombobox>
			<div class="floating-text">
				<div>{{ rightSideText }}</div>
				<slot name="right-side-prepend" />
			</div>
		</div>
		<ShareDialogQuickSuggestions
			class="mt-2"
			v-if="showSuggestions"
			@open-combobox="onOpenMenu"
			@select-item="addItemToSelection"
			:comboboxSelection="localComboboxSelection"
			:groupedTeamMembersToShareWith="groupedTeamMembersToShareWith"
		/>
	</Col>
</template>

<script>
import { DataTypeEvaluator } from '@ply-io/data-types';
import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import ChipOfContacts from './ChipOfContacts';
import ShareModuleDialogService from './share-module-dialog-service';
import AppCombobox from '@/components/common/AppCombobox/AppCombobox';
import ShareDialogQuickSuggestions from '@/components/dialogs/ShareFeatureDialog/ShareDialogQuickSuggestions.vue';
import ShareDialogInteractiveEmailInvite from '@/components/dialogs/ShareModuleDialog/ShareDialogInteractiveEmailInvite.vue';
import UserSelectComboboxItem from '@/components/dialogs/ShareModuleDialog/UserSelectComboboxItem.vue';
import { hasAccountPermission, teamMembers } from '@/store/modules/account/getters';
import { getGroups } from '@/store/modules/accountGroups/actions';
import { groups } from '@/store/modules/accountGroups/getters';
import { workflowSharedData } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
export default {
	name: 'UserSelectCombobox',
	components: {
		AppCombobox,
		ChipOfContacts,
		UserSelectComboboxItem,
		ShareDialogQuickSuggestions,
		ShareDialogInteractiveEmailInvite,
	},
	props: {
		workflowId: { type: Number },
		rightSideText: { type: String },
		showSuggestions: { type: Boolean },
		comboboxSelection: { type: Array },
		minimalBillingLevel: { type: String },
		includeEveryoneGroup: { type: Boolean },
		userIdsToExclude: { type: Array, default: () => [] },
		groupIdsToExclude: { type: Array, default: () => [] },
	},
	computed: {
		...mapGettersShorthand({ hasAccountPermission, groups, teamMembers, workflowSharedData }),
		localComboboxSelection: {
			get() {
				return this.comboboxSelection;
			},
			set(value) {
				this.$emit('update:comboboxSelection', value);
			},
		},
		canInviteToWorkspace() {
			return this.hasAccountPermission(AccountPermissions.InviteUsers);
		},
		placeholder() {
			return this.canInviteToWorkspace ? `Add teammates, groups or emails` : `Add teammates or groups`;
		},
		sharedWorkflowData() {
			return this.workflowSharedData(this.workflowId) || {};
		},
		fullSharedUsers() {
			return (this.sharedWorkflowData.shared_users || []).map((user) => user.id);
		},
		fullSharedGroups() {
			return (this.sharedWorkflowData.shared_groups || []).map((group) => group.id);
		},
		membersAlreadySharedWith() {
			return this.teamMembers.filter((u) => this.fullSharedUsers.includes(u.user_id) || this.userIdsToExclude.includes(u.user_id));
		},
		groupsAlreadySharedWith() {
			return this.groups.filter((g) => this.fullSharedGroups.includes(Number(g.id)) || this.groupIdsToExclude.includes(Number(g.id)));
		},
		relatedSyncedGroups() {
			return this.groups.filter((g) => (this.sharedWorkflowData.related_synced_groups || []).includes(Number(g.id)));
		},
		otherTeamMembers() {
			return this.teamMembers.filter((u) => u.email != this.user?.email);
		},
		groupedTeamMembersToShareWith() {
			return ShareModuleDialogService.getGroupedTeamMembersToShareWith(
				this.groups,
				this.teamMembers,
				this.otherTeamMembers,
				this.membersAlreadySharedWith,
				this.groupsAlreadySharedWith,
				this.relatedSyncedGroups,
				this.sharedWorkflowData,
				this.includeEveryoneGroup,
				this.canInviteToWorkspace
			);
		},
	},
	methods: {
		...mapActionsShorthand({ getGroups }),
		appendSearchToSelection() {
			if (this.canInviteToWorkspace) {
				const evaluator = new DataTypeEvaluator();
				const isCurrentSearchStringValidEmail = evaluator.isEmail(this.searchString);
				if (isCurrentSearchStringValidEmail) {
					this.localComboboxSelection = [...this.localComboboxSelection, this.searchString];
				}
			}
			this.searchString = '';
		},
		addItemToSelection(id) {
			this.$refs.user_combo.$el.blur();
			this.$nextTick(() => {
				const item = this.groupedTeamMembersToShareWith.find((item) => item.id === id);
				this.localComboboxSelection = [...this.localComboboxSelection, item];
			});
		},
		onOpenMenu() {
			this.$refs.user_combo?.$children?.[0]?.$refs?.input?.click?.();
		},
	},
	mounted() {
		this.getGroups();
	},
	data() {
		return { searchString: '' };
	},
};
</script>

<style scoped lang="scss">
.user-select-combobox {
	position: relative;

	::v-deep(.selection-chip) {
		.group-name {
			max-width: 200px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}

	::v-deep(.app-combobox) .v-input__icon--append {
		display: none;
	}

	::v-deep(.app-combobox) {
		.v-select__selections {
			padding-right: 210px !important; // in case #right-side-prepend is active

			input {
				padding-left: 20px;
				padding-right: 20px;
				font-size: 14px;
			}
		}
	}

	.floating-text {
		position: absolute;
		right: 10px;
		top: 20px;
		transform: translateY(-50%);
		color: var(--secondary-font-color);
		font-size: 14px;
		font-weight: 300;
	}
}
</style>
